import { useState, useCallback, useRef } from 'react';
import * as L from 'leaflet';
import { MAP_CONFIG } from '../constants/mapConfig';
import type { ClickMode } from '../models/click-mode';

interface MapInteractionState {
  mapCenter: [number, number];
  mapZoom: number;
  clickMode: ClickMode;
}

export const useMapInteractions = () => {
  const [mapCenter, setMapCenter] = useState<[number, number]>(MAP_CONFIG.DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(MAP_CONFIG.DEFAULT_ZOOM);
  const [clickMode, setClickMode] = useState<ClickMode>('select');
  const [placesUpdateKey, setPlacesUpdateKey] = useState(0);
  
  const loadTimeout = useRef<NodeJS.Timeout>();

  // Calculate radius based on zoom level
  const calculateRadiusFromZoom = useCallback((zoom: number): number => {
    return MAP_CONFIG.ZOOM_RADIUS_MAP[zoom as keyof typeof MAP_CONFIG.ZOOM_RADIUS_MAP] || 1000;
  }, []);

  // Handle map movement with debouncing
  const handleMapMove = useCallback((
    center: L.LatLng, 
    zoom: number,
    onPlacesLoad?: (center: L.LatLng, zoom: number) => void,
    onSelectionClear?: () => void
  ) => {
    const newCenter: [number, number] = [center.lat, center.lng];
    const oldCenter = mapCenter;

    // Calculate distance moved (only clear selection if map panned significantly, not just zoomed)
    const distanceMoved = Math.sqrt(
      Math.pow(newCenter[0] - oldCenter[0], 2) + Math.pow(newCenter[1] - oldCenter[1], 2)
    );
    
    // Only clear selection if map moved more than threshold
    const significantMove = distanceMoved > MAP_CONFIG.SIGNIFICANT_MOVE_THRESHOLD;
    
    setMapCenter(newCenter);
    setMapZoom(zoom);
    
    // Clear selection only on significant pan movement, not zoom
    if (significantMove && onSelectionClear) {
      onSelectionClear();
    }
    
    // Debounce place loading to avoid excessive API calls
    if (loadTimeout.current) {
      clearTimeout(loadTimeout.current);
    }
    
    loadTimeout.current = setTimeout(() => {
      if (onPlacesLoad) {
        onPlacesLoad(center, zoom);
      }
    }, MAP_CONFIG.PLACE_LOAD_DEBOUNCE_MS);
  }, [mapCenter]);

  const forceRefreshPlaces = useCallback(() => {
    setPlacesUpdateKey(prev => prev + 1);
  }, []);

  const enterAddDropPointMode = useCallback(() => {
    setClickMode('add-drop-point');
  }, []);

  const enterSelectMode = useCallback(() => {
    setClickMode('select');
  }, []);

  return {
    // State
    mapCenter,
    mapZoom,
    clickMode,
    placesUpdateKey,
    
    // Actions
    handleMapMove,
    forceRefreshPlaces,
    enterAddDropPointMode,
    enterSelectMode,
    calculateRadiusFromZoom,
    
    // Setters (for direct control if needed)
    setMapCenter,
    setMapZoom,
    setClickMode,
  };
};
